import { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import {
  fetchIntegrations,
  updateIntegration,
  clearIntegration,
  setIntegrationSource,
  fetchIntegrationPermissions,
  NO_INTEGRATION_PERMISSIONS,
  canManageCompanyIntegrations,
  canManageGlobalIntegrations,
  type Integration,
  type IntegrationPermissions,
  type IntegrationField,
  type IntegrationStatus,
  type IntegrationSource,
  type IntegrationVault,
} from "../services/integrationsService";
import {
  fetchMessagingStatus,
  type MessagingStatus,
  type CredentialSource,
} from "../services/chatService";
import { isPermissionDenied } from "../../../utils/api";
import { apiErrorCode } from "../../../utils/apiErrorCodes";

import { DIALOG_COLORS } from "../../../components/ui";
// ===========================================================================
// Configuración → Integraciones: store the client's API keys for the external
// providers the CRM connects to (ElevenLabs, Twilio, Anthropic, Google
// Calendar). Keys live in the backend `crm_integrations` collection; secrets
// come back masked and are never shown in full.
//
// Lo guardado aquí manda sobre el .env. Cada compañía elige, servicio por
// servicio, las llaves de la plataforma o las suyas; un administrador de
// plataforma además captura las compartidas en la vista «Llaves de la plataforma».
// ===========================================================================

const STATUS_META: Record<IntegrationStatus, { label: string; tone: string }> = {
  stored: { label: "Guardado", tone: "bg-emerald-500/15 text-emerald-500" },
  inherited: { label: "Heredado de la plataforma", tone: "bg-indigo-500/15 text-indigo-500" },
  env: { label: "Usando .env", tone: "bg-sky-500/15 text-sky-500" },
  unconfigured: { label: "Sin configurar", tone: "bg-gray-400/15 text-gray-500" },
};

export default function IntegrationsSettings() {
  const tc = buildThemeClasses();

  const [items, setItems] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Integration | null>(null);
  // #671 — write buttons only for the company's admin or a platform admin
  // (WAYAK keeps the keys per company).
  const [perms, setPerms] = useState<IntegrationPermissions>({
    loaded: false,
    isAdmin: false,
    companyId: null,
    permissions: NO_INTEGRATION_PERMISSIONS,
  });
  const [denied, setDenied] = useState(false);
  const canManage = canManageCompanyIntegrations(perms);
  const canManagePlatform = canManageGlobalIntegrations(perms);
  const [vault, setVault] = useState<IntegrationVault>("company");

  const load = async (target: IntegrationVault = vault) => {
    try {
      setItems(await fetchIntegrations(target));
      setDenied(false);
    } catch (e) {
      if (isPermissionDenied(e)) {
        setDenied(true);
        return;
      }
      Swal.fire({ icon: "error", title: "Error", text: "No se pudieron cargar las integraciones.", confirmButtonColor: brand.primary });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    fetchIntegrationPermissions().then(setPerms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchVault = (next: IntegrationVault) => {
    setVault(next);
    setLoading(true);
    load(next);
  };

  const chooseSource = async (integration: Integration, source: IntegrationSource) => {
    try {
      const updated = await setIntegrationSource(integration.id, source);
      setItems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (e) {
      // Cerrar el diálogo del código de verificación no es un fallo que anunciar.
      if (apiErrorCode(e) === "STEP_UP_REQUIRED") return;
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo cambiar el origen de las llaves.", confirmButtonColor: brand.primary });
    }
  };

  const handleSaved = (updated: Integration) => {
    setItems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    setEditing(null);
  };

  const confirmClear = async (integration: Integration) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Borrar claves guardadas",
      text: `Se eliminarán las claves guardadas de ${integration.name}.`,
      showCancelButton: true,
      confirmButtonText: "Borrar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    try {
      const updated = await clearIntegration(integration.id, vault);
      setItems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudieron borrar las claves.", confirmButtonColor: brand.primary });
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-500">
              <i className="ri-key-2-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Integraciones y API Keys</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Conecta tus proveedores (ElevenLabs, Twilio, Anthropic…) guardando aquí sus claves
              </p>
            </div>
          </div>
          <button
            onClick={() => { setLoading(true); load(); }}
            title="Recargar"
            className={`rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}
          >
            <i className="ri-refresh-line" />
          </button>
        </div>

        {/* Qué bóveda se ve: sólo un administrador de plataforma tiene dos. */}
        {canManagePlatform && (
          <div className="mb-3 flex flex-wrap gap-2" role="tablist" aria-label="Llaves que estás viendo">
            {([
              ["company", "Esta compañía"],
              ["platform", "Llaves de la plataforma"],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={vault === id}
                onClick={() => switchVault(id)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                  vault === id ? "border-primary-500 bg-primary-500/15 text-primary-500" : `${tc.border} ${tc.textSecondary} ${tc.buttonHover}`
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div className={`mb-5 flex items-start gap-2 rounded-xl border border-dashed p-3 text-xs ${tc.cardBorder} ${tc.textSecondary}`}>
          <i className="ri-information-line mt-0.5 text-sky-500" />
          <span>
            {vault === "platform"
              ? "Estas llaves las usa toda la plataforma: los leads y llamadas del CRM, y las compañías que elijan «Llave de la plataforma». Lo guardado aquí se aplica en menos de un minuto, sin redesplegar."
              : "En cada servicio elige si usas la llave de la plataforma o la tuya. Con la tuya, si falta algún dato el servicio queda sin configurar: nunca se cobra a la cuenta de la plataforma."}
          </span>
        </div>

        {/* Read-only notice (#671) */}
        {perms.loaded && !canManage && !denied && (
          <div className={`mb-5 flex items-start gap-2 rounded-xl border p-3 text-xs ${tc.cardBorder} ${tc.textSecondary}`}>
            <i className="ri-lock-line mt-0.5 text-amber-500" />
            <span>
              Sólo lectura: las claves de integración son de tu compañía y sólo su administrador (o un
              administrador de plataforma) puede cambiarlas o borrarlas. Pídele que lo haga por ti.
            </span>
          </div>
        )}

        {/* Provider cards */}
        {denied ? (
          <div className={`rounded-2xl border border-dashed p-10 text-center ${tc.cardBorder} ${tc.cardBg}`}>
            <i className="ri-lock-line mb-2 block text-2xl text-amber-500" />
            <p className={`text-sm ${tc.textSecondary}`}>
              No tienes permiso para ver las integraciones. Pide a un administrador que te lo otorgue.
            </p>
          </div>
        ) : loading ? (
          <div className={`rounded-2xl border border-dashed p-10 text-center ${tc.cardBorder} ${tc.cardBg}`}>
            <i className="ri-loader-4-line mb-2 block animate-spin text-2xl text-indigo-500" />
            <p className={`text-sm ${tc.textSecondary}`}>Cargando integraciones…</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {items.map((it) => (
              <ProviderCard
                key={it.id}
                integration={it}
                tc={tc}
                canManage={canManage && (it.scope !== "global" || canManagePlatform)}
                onEdit={() => setEditing(it)}
                onClear={() => confirmClear(it)}
                onChooseSource={(source) => chooseSource(it, source)}
              />
            ))}
          </div>
        )}

        {/* Meta messaging connection state (webhook + credentials + channels) */}
        <MetaMessagingStatusPanel tc={tc} />
      </div>

      {editing && canManage && (
        <Modal
          title={`Configurar ${editing.name}`}
          onClose={() => setEditing(null)}
          themeClasses={tc}
          content={
            <IntegrationForm
              tc={tc}
              integration={editing}
              vault={vault}
              onCancel={() => setEditing(null)}
              onSaved={handleSaved}
            />
          }
        />
      )}
    </div>
  );
}

// --- Pieces -----------------------------------------------------------------

type TC = ReturnType<typeof buildThemeClasses>;

function ProviderCard({
  integration, tc, canManage, onEdit, onClear, onChooseSource,
}: {
  integration: Integration;
  tc: TC;
  /** #671 — without write permission the buttons are not rendered at all. */
  canManage: boolean;
  onEdit: () => void;
  onClear: () => void;
  onChooseSource: (source: IntegrationSource) => void;
}) {
  const meta = STATUS_META[integration.status];
  const hasStored = integration.fields.some((f) => f.hasStored);

  return (
    <div className={`flex flex-col rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/15 text-indigo-500">
            <i className={`${integration.icon} text-lg`} />
          </span>
          <div className="min-w-0">
            <p className={`truncate text-sm font-semibold ${tc.text}`}>{integration.name}</p>
            <p className={`text-[12px] ${tc.textSecondary}`}>{integration.description}</p>
          </div>
        </div>
        <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.tone}`}>
          {meta.label}
        </span>
      </div>

      {integration.canChooseSource && (
        <label className={`mb-3 block text-xs ${tc.textSecondary}`}>
          ¿De quién son las llaves?
          <select
            value={integration.source ?? ""}
            disabled={!canManage}
            onChange={(e) => onChooseSource((e.target.value || null) as IntegrationSource)}
            className={`mt-1 w-full rounded-lg border bg-transparent px-2 py-1.5 text-xs ${tc.border} ${tc.text} disabled:opacity-60`}
          >
            <option value="">Automático (la propia si existe)</option>
            <option value="platform">Llave de la plataforma</option>
            <option value="own">Mi propia llave</option>
          </select>
        </label>
      )}

      <div className="flex-1 space-y-1.5">
        {integration.fields.map((f) => (
          <FieldRow key={f.key} field={f} tc={tc} />
        ))}
      </div>

      <div className={`mt-3 flex items-center justify-between gap-1 border-t pt-2 ${tc.border}`}>
        <a
          href={integration.docsUrl}
          target="_blank"
          rel="noreferrer"
          className={`text-[11px] ${tc.textSecondary} hover:text-indigo-500`}
        >
          <i className="ri-external-link-line mr-1" />Obtener claves
        </a>
        {canManage && (
          <div className="flex gap-1">
            {hasStored && (
              <button
                title="Borrar claves guardadas"
                onClick={onClear}
                className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
              >
                <i className="ri-delete-bin-line" />
              </button>
            )}
            <button
              onClick={onEdit}
              className="flex items-center gap-1.5 rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-medium text-on-primary hover:bg-primary-400"
            >
              <i className="ri-key-2-line" />Configurar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldRow({ field, tc }: { field: IntegrationField; tc: TC }) {
  // What value (if any) to display for this field.
  let display: string;
  let dim = false;
  if (field.type === "text" && field.value) display = field.value;
  else if (field.type === "secret" && field.preview) display = field.preview;
  else if (field.inherited) { display = "heredada de la plataforma"; dim = true; }
  else if (field.hasEnv) { display = "definido en .env"; dim = true; }
  else { display = "—"; dim = true; }

  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className={tc.textSecondary}>{field.label}</span>
      <code className={`max-w-[55%] truncate ${dim ? tc.textSecondary + " italic" : tc.text}`} title={display}>
        {display}
      </code>
    </div>
  );
}

// --- Meta messaging connection panel -----------------------------------------
// "Estado de conexión" for the meta_messaging provider: the webhook URL to
// paste into Meta, which credentials are present (and where each one comes
// from: .env vs guardada), and which channels can actually send right now.

/** API base the CRM services hit — same env the axios client is built from. */
const API_URL = String(import.meta.env.VITE_FIREBASE_API_URL ?? "").replace(/\/+$/, "");

const SOURCE_META: Record<"env" | "stored" | "none", { label: string; tone: string }> = {
  stored: { label: "Guardada", tone: "bg-emerald-500/15 text-emerald-500" },
  env: { label: ".env", tone: "bg-sky-500/15 text-sky-500" },
  none: { label: "—", tone: "bg-gray-400/15 text-gray-500" },
};

const SOURCE_KEYS: Array<{ key: keyof MessagingStatus["sources"]; label: string }> = [
  { key: "appSecret", label: "App Secret" },
  { key: "verifyToken", label: "Verify Token" },
  { key: "whatsappToken", label: "Token de WhatsApp" },
  { key: "whatsappPhoneNumberId", label: "Phone Number ID" },
  { key: "whatsappBusinessAccountId", label: "Business Account ID" },
];

function SourceChip({ source }: { source: CredentialSource }) {
  const meta = SOURCE_META[source ?? "none"];
  return (
    <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.tone}`}>
      {meta.label}
    </span>
  );
}

function CheckRow({ ok, label, hint, tc }: { ok: boolean; label: string; hint: string; tc: TC }) {
  return (
    <div className="flex items-start gap-2">
      <i className={ok ? "ri-checkbox-circle-fill mt-0.5 text-emerald-500" : "ri-error-warning-fill mt-0.5 text-amber-500"} />
      <div className="min-w-0">
        <p className={`text-sm font-medium ${tc.text}`}>{label}</p>
        <p className={`text-xs ${tc.textSecondary}`}>{hint}</p>
      </div>
    </div>
  );
}

function ChannelChip({ icon, label, canSend, tc }: { icon: string; label: string; canSend: boolean; tc: TC }) {
  return (
    <span
      className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
        canSend ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : `${tc.border} ${tc.textSecondary}`
      }`}
      title={canSend ? `${label}: listo para enviar` : `${label}: falta el token para enviar`}
    >
      <i className={icon} />
      {label}
      <i className={canSend ? "ri-checkbox-circle-fill" : "ri-close-circle-line"} />
    </span>
  );
}

function MetaMessagingStatusPanel({ tc }: { tc: TC }) {
  const [status, setStatus] = useState<MessagingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus(await fetchMessagingStatus());
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudo cargar el estado de la conexión");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      Swal.fire({ icon: "success", title: "Copiado", timer: 1200, showConfirmButton: false });
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  const webhookUrl = status ? `${API_URL}${status.webhookPath}` : "";

  return (
    <div className={`mt-6 rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-500">
            <i className="ri-plug-line text-lg" />
          </span>
          <div>
            <p className={`text-sm font-semibold ${tc.text}`}>Estado de conexión — Mensajería (Meta)</p>
            <p className={`text-[12px] ${tc.textSecondary}`}>
              Webhook, credenciales y canales de WhatsApp · Messenger · Instagram
            </p>
          </div>
        </div>
        <button
          onClick={load}
          title="Recargar estado"
          className={`rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}
        >
          <i className="ri-refresh-line" />
        </button>
      </div>

      {loading ? (
        <div className={`py-6 text-center text-sm ${tc.textSecondary}`}>
          <i className="ri-loader-4-line mr-2 animate-spin" />
          Cargando estado…
        </div>
      ) : error ? (
        <div className="py-4 text-center">
          <i className="ri-error-warning-line mb-2 block text-2xl text-red-500" />
          <p className="text-sm text-red-500">{error}</p>
          <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-white hover:bg-primary-400">
            Reintentar
          </button>
        </div>
      ) : status ? (
        <div className="space-y-4">
          {/* Webhook URL to paste into Meta */}
          <div className={`rounded-xl border p-3 ${tc.border}`}>
            <p className={`mb-1 text-xs font-medium ${tc.textSecondary}`}>
              URL de devolución de llamada (Webhook) — pégala en Meta → App → Webhooks → campo <b>messages</b>
            </p>
            <div className="flex items-center gap-2">
              <code className={`flex-1 truncate rounded-lg bg-black/5 px-2 py-1.5 text-xs dark:bg-white/10 ${tc.text}`}>
                {webhookUrl}
              </code>
              <button
                onClick={() => copy(webhookUrl)}
                className={`rounded-lg border px-3 py-1.5 text-xs ${tc.border} ${tc.textSecondary} hover:bg-primary-500/10`}
                title="Copiar URL"
              >
                <i className="ri-file-copy-line" />
              </button>
            </div>
            <p className={`mt-2 text-xs ${tc.textSecondary}`}>
              Usa el mismo <b>Verify Token</b> configurado abajo al verificar la suscripción. Graph API {status.graphVersion}.
            </p>
          </div>

          {/* Config checklist */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <CheckRow ok={status.appSecretSet} label="App Secret" hint="Verifica la firma de los webhooks entrantes." tc={tc} />
            <CheckRow ok={status.verifyTokenSet} label="Verify Token" hint="Para el handshake de suscripción del webhook." tc={tc} />
            <CheckRow ok={status.templatesConfigured} label="Plantillas" hint="Business Account ID + token de WhatsApp para listar y enviar plantillas." tc={tc} />
          </div>

          {/* Per-channel send capability */}
          <div>
            <p className={`mb-1.5 text-xs font-medium ${tc.textSecondary}`}>Canales de envío</p>
            <div className="flex flex-wrap gap-1.5">
              <ChannelChip icon="ri-whatsapp-line" label="WhatsApp" canSend={status.channels.whatsapp.canSend} tc={tc} />
              <ChannelChip icon="ri-messenger-line" label="Messenger" canSend={status.channels.messenger.canSend} tc={tc} />
              <ChannelChip icon="ri-instagram-line" label="Instagram" canSend={status.channels.instagram.canSend} tc={tc} />
            </div>
            {status.channels.whatsapp.canSend && !status.channels.whatsapp.phoneNumberId && (
              <p className={`mt-1.5 text-[11px] ${tc.textSecondary}`}>
                <i className="ri-information-line mr-0.5 text-amber-500" />
                WhatsApp tiene token pero falta el <b>Phone Number ID</b> para poder enviar.
              </p>
            )}
          </div>

          {/* Where each credential comes from */}
          <div>
            <p className={`mb-1.5 text-xs font-medium ${tc.textSecondary}`}>Origen de cada credencial</p>
            <div className="space-y-1.5">
              {SOURCE_KEYS.map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between gap-2 text-xs">
                  <span className={tc.textSecondary}>{label}</span>
                  <SourceChip source={status.sources[key]} />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function IntegrationForm({
  tc, integration, vault, onSaved, onCancel,
}: {
  tc: TC;
  integration: Integration;
  vault: IntegrationVault;
  onSaved: (updated: Integration) => void;
  onCancel: () => void;
}) {
  // Text fields are pre-filled (visible/editable); secret fields start empty and
  // are only sent when the user types something (or explicitly clears them).
  const [inputs, setInputs] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const f of integration.fields) seed[f.key] = f.type === "text" ? (f.value ?? "") : "";
    return seed;
  });
  const [clearedSecrets, setClearedSecrets] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content`;
  const labelClass = `mb-1 block text-xs font-medium ${tc.textSecondary}`;

  const set = (k: string, v: string) => setInputs((p) => ({ ...p, [k]: v }));
  const toggleClear = (k: string) =>
    setClearedSecrets((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });

  const submit = async () => {
    const values: Record<string, string> = {};
    for (const f of integration.fields) {
      const typed = (inputs[f.key] ?? "").trim();
      if (f.type === "text") {
        values[f.key] = typed; // always sent; empty clears
      } else if (clearedSecrets.has(f.key)) {
        values[f.key] = ""; // explicit clear
      } else if (typed) {
        values[f.key] = typed; // new/updated secret
      }
      // secret left blank & not cleared → omit (keep the stored value)
    }

    setSaving(true);
    try {
      const updated = await updateIntegration(integration.id, values, vault);
      onSaved(updated);
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo guardar la integración.", confirmButtonColor: brand.primary });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {integration.fields.map((f) => {
        const cleared = clearedSecrets.has(f.key);
        const placeholder =
          f.type === "secret" && f.hasStored && !cleared
            ? `${f.preview} (guardado — deja en blanco para conservar)`
            : f.hasEnv && !f.hasStored
              ? "definido en .env — escribe para sobreescribir"
              : f.placeholder;
        return (
          <div key={f.key}>
            <div className="flex items-center justify-between">
              <label className={labelClass}>{f.label}</label>
              {f.type === "secret" && f.hasStored && (
                <button
                  type="button"
                  onClick={() => toggleClear(f.key)}
                  className={`mb-1 text-[11px] ${cleared ? "text-red-500" : tc.textSecondary} hover:text-red-500`}
                >
                  <i className={`mr-0.5 ${cleared ? "ri-close-circle-line" : "ri-delete-bin-line"}`} />
                  {cleared ? "Se borrará" : "Borrar"}
                </button>
              )}
            </div>
            <input
              type={f.type === "secret" ? "password" : "text"}
              autoComplete="off"
              className={`${inputClass} ${cleared ? "opacity-50" : ""}`}
              value={inputs[f.key] ?? ""}
              disabled={cleared}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={placeholder}
            />
          </div>
        );
      })}

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}>
          Cancelar
        </button>
        <button
          onClick={submit}
          disabled={saving}
          className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400 disabled:opacity-60"
        >
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </div>
  );
}
